import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Search, Plus, Edit, Eye, Trash2, Filter, RefreshCw } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import AdminNavbar from "../../../AdminNavbar";
import { useOptimisticDoctors } from "../../../../hooks/useOptimisticUpdates";
import { DoctorTableSkeleton } from "../../../ui/table-skeleton";

const AdminDoctorsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterCity, setFilterCity] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Use optimistic updates hook
  const { 
    doctors, 
    loading: isLoading,
    deleteDoctorOptimistic, 
    refreshDoctors 
  } = useOptimisticDoctors();

  // Initial data load
  useEffect(() => {
    refreshDoctors(true);
  }, [refreshDoctors]);

  // Filter doctors based on search and filters
  const filteredDoctors = useMemo(() => {
    if (!Array.isArray(doctors)) {
      return [];
    }
    
    let filtered = doctors;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (doctor) =>
          doctor.fullName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          doctor.specialty
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          doctor.mainCategory
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          doctor.about
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          doctor.cities?.some(city => 
            city?.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    // Filter by category
    if (filterCategory !== "all") {
      filtered = filtered.filter(
        (doctor) => doctor.mainCategory === filterCategory
      );
    }

    // Filter by city
    if (filterCity !== "all") {
      filtered = filtered.filter(
        (doctor) => 
          doctor.cities?.includes(filterCity)
      );
    }

    return filtered;
  }, [doctors, searchTerm, filterCategory, filterCity]);

  const getUniqueCategories = () => {
    if (!Array.isArray(doctors)) {
      return [];
    }
    const categories =
      doctors.map((doctor) => doctor.mainCategory).filter(Boolean);
    return [...new Set(categories)];
  };

  const getUniqueCities = () => {
    if (!Array.isArray(doctors)) {
      return [];
    }
    const cities = doctors
      .flatMap((doctor) => doctor.cities || [])
      .filter(Boolean);
    return [...new Set(cities)];
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (window.confirm("Are you sure you want to delete this doctor?")) {
      try {
        await deleteDoctorOptimistic(doctorId);
      } catch (error) {
        console.error('Error deleting doctor:', error);
      }
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshDoctors();
    } finally {
      setIsRefreshing(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Doctors</h1>
            <p className="text-gray-600 mt-2">
              View and manage all doctor profiles
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Link to="/admin/doctors/create">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add New Doctor
              </Button>
            </Link>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Search & Filters</CardTitle>
            <CardDescription>
              Find specific doctors using search and filters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search doctors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {getUniqueCategories().map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterCity} onValueChange={setFilterCity}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  <SelectItem value="Faridabad">Faridabad</SelectItem>
                  <SelectItem value="Delhi">Delhi</SelectItem>
                  <SelectItem value="Gurgaon">Gurgaon</SelectItem>
                  <SelectItem value="Noida">Noida</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setFilterCategory("all");
                  setFilterCity("all");
                }}
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Doctors Table */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-xl font-semibold">Doctors ({filteredDoctors.length})</CardTitle>
                <CardDescription className="mt-1">
                  Showing {filteredDoctors.length} of {Array.isArray(doctors) ? doctors.length : 0} doctors
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  Total: {Array.isArray(doctors) ? doctors.length : 0}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6">
                <DoctorTableSkeleton />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/50 border-b">
                        <TableHead className="font-semibold text-gray-700 py-4">Doctor Information</TableHead>
                        <TableHead className="font-semibold text-gray-700">Specialty & Category</TableHead>
                        <TableHead className="font-semibold text-gray-700">Location</TableHead>
                        <TableHead className="font-semibold text-gray-700">Experience & Fees</TableHead>
                        <TableHead className="font-semibold text-gray-700">Rating</TableHead>
                        <TableHead className="font-semibold text-gray-700">Status & Type</TableHead>
                        <TableHead className="font-semibold text-gray-700 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDoctors.map((doctor, index) => (
                        <TableRow key={doctor._id} className={`hover:bg-gray-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'}`}>
                          <TableCell className="py-4">
                            <div className="flex items-center space-x-4">
                              <div className="relative">
                                <img
                                  src={
                                    doctor.profileImageUrl ||
                                    "/placeholder-doctor.jpg"
                                  }
                                  alt={doctor.fullName}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                                />
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-gray-900 truncate">
                                  {doctor.professionalTitle} {doctor.fullName}
                                </div>
                                <div className="text-sm text-gray-600 truncate">
                                  {doctor.specialty}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Employee ID: {doctor.employeeCode || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="space-y-2">
                              <Badge variant="secondary" className="font-medium">
                                {doctor.mainCategory}
                              </Badge>
                              <div className="text-xs text-gray-600">
                                {doctor.specialty}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="space-y-1">
                              <div className="font-medium text-gray-900">
                                {doctor.cities?.length > 0 ? doctor.cities.join(", ") : "N/A"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="space-y-1">
                              <div className="font-medium text-gray-900">
                                {doctor.experience || "N/A"}
                              </div>
                              <div className="text-sm text-green-600 font-semibold">
                                ₹{doctor.consultationFees || 0}
                              </div>
                              <div className="text-xs text-gray-500">
                                Consultation Fee
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="flex items-center space-x-1">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <span
                                    key={i}
                                    className={`text-lg ${
                                      i < Math.floor(doctor.rating || 0)
                                        ? 'text-yellow-400'
                                        : 'text-gray-300'
                                    }`}
                                  >
                                    ★
                                  </span>
                                ))}
                              </div>
                              <span className="text-sm font-medium text-gray-700 ml-2">
                                {doctor.rating || 0}/5
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {doctor.reviewCount || 0} reviews
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="space-y-2">
                              <Badge
                                variant={
                                  doctor.status === "active"
                                    ? "default"
                                    : "destructive"
                                }
                                className="font-medium"
                              >
                                {doctor.status?.toUpperCase()}
                              </Badge>
                              <div className="flex flex-wrap gap-1">
                                {doctor.isVisitingDoctor && (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    🏠 Visiting
                                  </Badge>
                                )}
                                {doctor.isHospitalDoctor && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                    🏥 Hospital
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="flex justify-center space-x-1">
                              <Link to={`/admin/doctors/${doctor._id}`}>
                                <Button size="sm" variant="outline" className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-300">
                                  <Eye className="h-3.5 w-3.5 text-blue-600" />
                                </Button>
                              </Link>
                              <Link to={`/admin/doctors/${doctor._id}/edit`}>
                                <Button size="sm" variant="outline" className="h-8 w-8 p-0 hover:bg-green-50 hover:border-green-300">
                                  <Edit className="h-3.5 w-3.5 text-green-600" />
                                </Button>
                              </Link>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0 hover:bg-red-50 hover:border-red-300"
                                onClick={() => handleDeleteDoctor(doctor._id)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {filteredDoctors.length === 0 && !isLoading && (
                  <div className="text-center py-12 px-4 border-t">
                    <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Search className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No doctors found</h3>
                    <p className="text-gray-500 text-sm mb-4">
                      {searchTerm || filterCategory !== "all" || filterCity !== "all" 
                        ? "Try adjusting your search criteria or filters" 
                        : "No doctors have been added yet"}
                    </p>
                    {!searchTerm && filterCategory === "all" && filterCity === "all" && (
                      <Link to="/admin/doctors/create">
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Doctor
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDoctorsList;
